/*
 * <everything-in-the-box> — desktop drag-to-pan for the accessory rail.
 * Native overflow-x handles wheel/trackpad/keyboard. This class adds:
 *   1. Mouse/pen drag-to-pan that mirrors trackpad feel (no card-to-card
 *      snapping; scroll moves 1:1 with the cursor).
 *   2. Flick inertia — after a fast release we continue the scroll with
 *      an ease-out decay so short drags can still glide.
 *   3. A click-swallow guard so a drag that crosses an interactive child
 *      doesn't fire a click on release.
 * Touch is left to native scrolling on purpose.
 */
if (!customElements.get('everything-in-the-box')) {
  customElements.define('everything-in-the-box', class EverythingInTheBox extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-ebox-drag]');
      if (!this.scroller) return;
      this.setupDrag(this.scroller);
    }

    disconnectedCallback() {
      if (this.inertiaRaf) cancelAnimationFrame(this.inertiaRaf);
    }

    setupDrag(el) {
      const DRAG_THRESHOLD = 4;
      const FRICTION = 0.94;
      const MIN_VELOCITY = 0.4;

      let isDown = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;
      let activePointerId = null;
      let lastX = 0;
      let lastT = 0;
      let velocity = 0;

      const cancelInertia = () => {
        if (this.inertiaRaf) {
          cancelAnimationFrame(this.inertiaRaf);
          this.inertiaRaf = null;
        }
      };

      el.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') return;
        if (e.button !== 0) return;
        if (e.target.closest('a, button, [role="button"], input, select, textarea, label')) return;

        cancelInertia();
        isDown = true;
        moved = false;
        activePointerId = e.pointerId;
        startX = e.clientX;
        lastX = e.clientX;
        lastT = performance.now();
        startScroll = el.scrollLeft;
        velocity = 0;
        el.classList.add('is-dragging');
        try { el.setPointerCapture(e.pointerId); } catch (_) {}
      });

      el.addEventListener('pointermove', (e) => {
        if (!isDown || e.pointerId !== activePointerId) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > DRAG_THRESHOLD) moved = true;
        if (moved) {
          el.scrollLeft = startScroll - dx;

          const now = performance.now();
          const dt = now - lastT;
          if (dt > 0) {
            const instantV = (e.clientX - lastX) / dt;
            velocity = velocity * 0.7 + instantV * 0.3;
          }
          lastX = e.clientX;
          lastT = now;
          e.preventDefault();
        }
      });

      const endDrag = () => {
        if (!isDown) return;
        isDown = false;
        el.classList.remove('is-dragging');
        try { el.releasePointerCapture(activePointerId); } catch (_) {}
        activePointerId = null;

        if (moved) {
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          el.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => el.removeEventListener('click', swallow, { capture: true }), 50);

          if (Math.abs(velocity) > MIN_VELOCITY) {
            let v = -velocity * 16;
            const step = () => {
              el.scrollLeft += v;
              v *= FRICTION;
              if (Math.abs(v) > 0.5 &&
                  el.scrollLeft > 0 &&
                  el.scrollLeft < el.scrollWidth - el.clientWidth) {
                this.inertiaRaf = requestAnimationFrame(step);
              } else {
                this.inertiaRaf = null;
              }
            };
            this.inertiaRaf = requestAnimationFrame(step);
          }
        }
      };
      el.addEventListener('pointerup', endDrag);
      el.addEventListener('pointercancel', endDrag);
      el.addEventListener('pointerleave', endDrag);

      el.addEventListener('wheel', cancelInertia, { passive: true });
      el.addEventListener('dragstart', (e) => e.preventDefault());
    }
  });
}
